import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Popper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useMemo, useRef, useEffect } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { getTemplateColumnColumns } from "../../const/AssertData";
import { assetFamilyMaster, AssetSelection, TemplateColumnMaster } from "../../types/assetTypes";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { getFamilyAssetMasterDataList, getTemplateColumnById, getTemplateColumnMasterDataList, saveTemplateColumn } from "../../api/Services/masterServie";
import { FILE_DOWNLOAD_PREVIEW, PAGESEND } from "../../api/endpoint";
import { base64ToFile } from "../../const/const";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Logo from "../../assets/companyLogo.png";
import { getAssetNamesFamilyId, getUniqueAssetNamesByFamilyId, uploadFiles } from "../../api/Services/assetServices";
import Camera, { FACING_MODES, IMAGE_TYPES } from 'react-html5-camera-photo';
import ChooseCam from "@mui/icons-material/Camera";
import { RootState } from "../../store/Reduxstore";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";

interface AssetTemplateColumnProps {
  setPathname: (path: string) => void;
}


const TemplateColumn: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<TemplateColumnMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [assetTemplateColumnData, setAssetTemplateColumnData] = useState<TemplateColumnMaster>({
    templateColumnId: 0,
    templateColumnName: "",
    attachments: [],
    assets: [],   // <-- use this instead of familyAssetIds + assetName
  });


  // Memoized column definition
  const columns = useMemo(() => getTemplateColumnColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditFamilyAsset(rowData.templateColumnId)}
        />
      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );

  const handleEditFamilyAsset = async (templateColumnId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getTemplateColumnById(templateColumnId);
      let { data, status } = response;

      if (status.code === 200 && data) {
        setAssetTemplateColumnData(data); // set template column data first
        setOpenDialog(true);

        // ✅ Fetch asset names for each familyAssetId
        if (data.familyAssetIds && data.familyAssetIds.length > 0) {
          await getAssetNameList(data.familyAssetIds);
        } else {
          setAssetNameList([]); // no families selected
        }

      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };


  const [familyAsset, setFamilyAsset] = useState<assetFamilyMaster[]>([]);
  const [assetName, setAssetNameList] = useState<
    { familyAssetId: number; value: string; label: string }[]
  >([]);

  useEffect(() => {
    console.log("Updated assetName state:", assetName);
  }, [assetName]);

  useEffect(() => {
    if (familyAsset.length === 0) getFamilyAssetList(1, PAGESEND);
  }, [familyAsset]);

  const getFamilyAssetList = async (page: number, rows: number) => {
    dispatch(setLoading(true));
    try {
      const { data, status } = await getFamilyAssetMasterDataList(page, rows, { familyAssetId: { value: "", matchMode: "startWith" } });
      if (status.code === 200 && data?.length > 0) {
        setFamilyAsset(data);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };
  const getAssetNameList = async (familyAssetIds: number[]) => {
    dispatch(setLoading(true));
    try {
      let allAssets: { familyAssetId: number; assetName: string }[] = [];

      for (const id of familyAssetIds) {
        const { data, status } = await getAssetNamesFamilyId(id);
        if (status.code === 200 && Array.isArray(data)) {
          allAssets = [...allAssets, ...data]; // data already has {familyAssetId, assetName}
        }
      }

      if (allAssets.length > 0) {
        // ✅ keep unique assetName + familyAssetId combo
        const uniqueAssets = Array.from(
          new Map(allAssets.map(a => [`${a.familyAssetId}-${a.assetName}`, a])).values()
        );

        // ✅ format for dropdown but keep familyAssetId internally
        const updatedData = uniqueAssets.map((a) => ({
          familyAssetId: a.familyAssetId,
          value: a.assetName,
          label: a.assetName,
        }));

        setAssetNameList(updatedData);
        console.log("Asset name list set:", updatedData);

        // ✅ cleanup assets in template state when families change
        setAssetTemplateColumnData((prev) => {
          const filteredAssets = prev.assets?.filter(a =>
            familyAssetIds.includes(a.familyAssetId)
          ) || [];

          return {
            ...prev,
            familyAssetIds,
            assets: filteredAssets,
          };
        });
      } else {

        setAssetNameList([]);
        setAssetTemplateColumnData((prev) => ({
          ...prev,
          familyAssetIds,
          assets: [],
        }));
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };





  // Generate filters dynamically

  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } =
        await getTemplateColumnMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setAssetTemplateColumnData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setAssetTemplateColumnData({
      templateColumnId: 0,
      templateColumnName: "",
      attachments: [],
      assets: []
    });
  };

  interface Location {
    latitude: number;
    longitude: number;
  }

  const removeFile = (index: number) => {
    const updatedAttachments = [...assetTemplateColumnData.attachments];
    updatedAttachments.splice(index, 1);
    setAssetTemplateColumnData({ ...assetTemplateColumnData, attachments: updatedAttachments });
  };


  interface TakeCameraProps {
    assetTemplateData: TemplateColumnMaster; // Form data structure
    setAssetTemplateData: React.Dispatch<React.SetStateAction<TemplateColumnMaster>>; // Function to set form data
  }

  const TakeCamera: React.FC<TakeCameraProps> = ({ assetTemplateData, setAssetTemplateData }) => {
    const [location, setLocation] = useState<Location | null>(null);
    const [imgsrc, setImgSrc] = useState<string | null>(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [openCam, setOpenCam] = useState(false)
    const streamRef = useRef<MediaStream | null>(null);

    useEffect(() => {
      getLocation();
    }, []);

    const watermark = new Image();
    watermark.crossOrigin = "anonymous";
    watermark.src = Logo;
    watermark.onload = function () {
      const canvas = document.createElement("canvas");
      canvas.width = watermark.width;
      canvas.height = watermark.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(watermark, 0, 0);
      }
      const imgData = canvas?.toDataURL("image/png");
    };
    watermark.onerror = function () {
      console.error("Failed to load watermark image.");
    };

    interface CapturePhotoProps {
      dataUri: string;
    }

    const capturePhoto = async ({ dataUri }: CapturePhotoProps): Promise<void> => {
      try {
        const img = new Image();

        const loadImage = new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("Failed to load image"));
          img.src = dataUri;
        });

        await loadImage;

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const context = canvas.getContext("2d");

        if (context) {
          context.drawImage(img, 0, 0);

          const timestamp = new Date().toLocaleString();
          const gpsLocation = `${location?.latitude},${location?.longitude}`;

          context.font = "18px Arial";
          context.fillStyle = "white";
          context.fillText(timestamp, 10, 30);
          context.fillText(gpsLocation, 10, 60);

          const logoWidth = 130; // Adjust the logo size as needed
          const logoHeight = 70;
          const logoX = canvas.width - logoWidth - 25; // Adjust the position as needed
          const logoY = canvas.height - logoHeight - 25;
          context.drawImage(watermark, logoX, logoY, logoWidth, logoHeight);
        }
        const photo = canvas.toDataURL();
        setCameraOpen(false);
        setImgSrc(photo);
      } catch (error: any) {
        CustomMessage(`Error capturing photo:${error?.message}`, "error", enqueueSnackbar)
        console.error("Error capturing photo:", error);
      }
    };

    const handleLocation = (position: GeolocationPosition) => {
      setLocation(position.coords);
    };

    const requestCameraAccess = async (video: boolean) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: video });
        // Use the camera stream as needed
        streamRef.current = stream; // Store the stream reference
      } catch (error: any) {
        console.error("Error accessing camera:", error);
        CustomMessage(`Error accessing camera:${error?.message}`, 'error', enqueueSnackbar);
        const tracks = streamRef.current?.getTracks() || [];
        tracks.forEach((track) => track.stop());
      }
    };

    const stopCamera = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop()); // Stop all tracks
        streamRef.current = null; // Clear the reference
      }
      setCameraOpen(false);
      setOpenCam(false);
    };

    const handleError = (error: any) => {
      CustomMessage(error?.message, 'error', enqueueSnackbar);
      console.error(error);
    };

    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(handleLocation, handleError);
      } else {
        CustomMessage("Geolocation is not supported by this browser", 'error', enqueueSnackbar);
        console.error("Geolocation is not supported by this browser");
      }
    };

    const openCamera = async () => {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(() => {
            try {
              requestCameraAccess(true);
              setCameraOpen(true);
              setOpenCam(true);
              // setImgSrc(null);
            } catch (error) {
              console.error("Error opening camera:", error);
              setCameraOpen(false);
              setOpenCam(false);
            }
          })
          .catch((error: any) => {
            // Handle camera access error
            CustomMessage(`Camera access denied:${error?.message}`, 'error', enqueueSnackbar);
            console.error('Camera access denied:', error);
          });
      }
    };

    const uploadImage = async () => {
      if (!imgsrc) {
        CustomMessage("Image not generated", 'error', enqueueSnackbar);
        return;
      }
      dispatch(setLoading(true));
      try {
        const file = base64ToFile({ base64: imgsrc, filename: 'Stock.png' });

        const data = new FormData();
        data.append('file', file); // Ensure the key matches backend expectations       

        const response = await uploadFiles(data);
        if (response.status.code === 201) {
          const updatedFormData = { ...assetTemplateData };
          updatedFormData.attachments = [...updatedFormData.attachments, response.data];
          setAssetTemplateData(updatedFormData);

          stopCamera();
        } else {
          CustomMessage(`Upload failed with status: ${response.status.code}`, 'error', enqueueSnackbar);
        }
      } catch (error: any) {
        CustomMessage(error?.message, 'error', enqueueSnackbar);
      } finally {
        dispatch(setLoading(false));
      }
    }

    return (
      <div>
        <ReusablePrimeReactDialog
          header="Take Photo"
          visible={openCam}
          onHide={stopCamera}
          footer={
            <div>
              {!cameraOpen &&
                <>
                  <ReusablePrimeButton severity="secondary" onClick={stopCamera}>Cancel</ReusablePrimeButton>
                  <ReusablePrimeButton severity="info" onClick={uploadImage}>Upload</ReusablePrimeButton>
                </>
              }
            </div>
          }
        >
          {
            cameraOpen ?
              <Camera
                onTakePhoto={(dataUri: string) => capturePhoto({ dataUri })}
                idealFacingMode="environment"
                isFullscreen={false}
                idealResolution={{ width: 640, height: 480 }}
                imageType={IMAGE_TYPES.PNG}
                videoStream={streamRef.current}
              />
              :
              imgsrc && <img src={imgsrc} alt="logo" />
          }
        </ReusablePrimeReactDialog>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Box
            onClick={openCamera}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: 2,
              border: "1px dashed gray",
              borderRadius: "8px",
              width: "100%",
              maxWidth: "300px",
              textAlign: "center",
              justifyContent: "center",
            }}
          >
            <ChooseCam />
            <span>Take photo</span>
          </Box>
          <ListItem>
            {assetTemplateData.attachments.map((attachment, index) => (
              <div key={index} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box display="flex" flexDirection="row" gap={1}>
                  {/* Your content */}
                  <span>{attachment.name}</span>
                </Box>
              </div>
            ))}
          </ListItem>
        </Box>
      </div>
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (assetTemplateColumnData.templateColumnName === "") {
      CustomMessage("Add the name", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveTemplateColumn(assetTemplateColumnData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        CustomMessage(status.message, "success", enqueueSnackbar);
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) {
      CustomMessage("No file selected", 'error', enqueueSnackbar);
      return;
    }

    const fileArray = Array.from(fileList); // Convert FileList to an array

    // Filter only image files
    const imageFiles = fileArray.filter(file =>
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      CustomMessage("Only image file is allowed", 'error', enqueueSnackbar);
      return;
    }

    dispatch(setLoading(true)); // Set loading before upload starts

    try {
      const uploadPromises = imageFiles.map(async (file) => {
        if (!file) {
          CustomMessage("File not found", 'error', enqueueSnackbar);
          return;
        }

        const data = new FormData();
        data.append('file', file); // Make sure the backend expects 'file'

        const response = await uploadFiles(data);

        if (response.status.code === 201) {
          setAssetTemplateColumnData((prev) => ({
            ...prev,
            attachments: Array.isArray(prev.attachments)
              ? [...prev.attachments, { ...response.data }]
              : [{ ...response.data }]
          }));
          return response.data;
        } else {
          CustomMessage(`Upload failed with status: ${response.status.code}`, 'error', enqueueSnackbar);
          throw new Error(`Upload failed with status: ${response.status.code}`);
        }
      });

      await Promise.all(uploadPromises);
      CustomMessage("All files uploaded successfully", 'success', enqueueSnackbar);

    } catch (error: any) {
      CustomMessage(`File upload error: ${error.message}`, 'error', enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSelectAssetNameChange = (e: SelectChangeEvent<any>) => {
    const selectedValues = e.target.value; // array of selected values (e.g., ['Pump', 'Valve'])
    const updatedData = { ...assetTemplateColumnData, assetName: selectedValues };
    setAssetTemplateColumnData(updatedData);
  };

  const userDetails = useSelector((state: RootState) => state.auth)

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h2
          style={{
            margin: "0 0 12px 8px",
            fontWeight: 500,
            fontSize: "18px",
          }}
        >
          Service Template Column
        </h2>

        {userDetails.role.some(
          (r) =>
            r.roleName === "Admin" ||
            r.roleName === "Service Template Column Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>

      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log("Double-click row:", rowData);
            handleEditFamilyAsset(rowData.templateColumnId);
          }}
        />
      </Box>

      <ReusablePrimeReactDialog
        header="Add Service Template Column"
        visible={openDialog}
        onHide={handleClear}
        style={{ overflow: "hidden" }}
      >
        <form
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault(); // prevent accidental submit
            }
          }}
        >
          {/* Template Column Name */}
          <div className="mb-3">
            <h6>Service Template Column Name</h6>
            <TextField
              fullWidth
              variant="outlined"
              value={assetTemplateColumnData.templateColumnName}
              name="templateColumnName"
              onChange={handleChange}
              required
            />
          </div>

          {/* Select Families */}
          <div className="mb-3">
            <h6>Select Families</h6>
            <MultiSelect
              value={assetTemplateColumnData.familyAssetIds}
              options={familyAsset.map((f) => ({
                label: f.familyName,
                value: f.familyAssetId,
              }))}
              optionLabel="label"
              optionValue="value"
              placeholder="Select Families"
              className="w-full"
              style={{ width: "100%" }}
              panelClassName="w-full"
              appendTo="self"
              filter
              onChange={(e: MultiSelectChangeEvent) => {
                const ids = e.value as number[];
                getAssetNameList(ids);

                setAssetTemplateColumnData((prev) => {
                  const safeAssets = prev?.assets ?? []; // 👈 always array

                  const filteredAssets = safeAssets.filter((a) =>
                    ids.includes(a.familyAssetId)
                  );

                  return {
                    ...prev,
                    familyAssetIds: ids,   // keep families in sync
                    assets: filteredAssets,
                  };
                });
              }}



            />
          </div>

          {/* Asset Types */}
          <div className="mb-3">
            <h6>Asset Types</h6>

            <MultiSelect
              value={assetTemplateColumnData.assets?.map(a => a.assetName) || []} // ✅ string[]
              options={assetName.map((type) => ({
                label: type.label,
                value: type.value,
              }))}

              optionLabel="label"
              optionValue="value"
              placeholder="Select Asset Types"
              className="w-full"
              style={{ width: "100%" }}
              panelClassName="w-full"
              appendTo="self"
              filter
              onChange={(e: MultiSelectChangeEvent) => {
                const selectedAssets = e.value as string[];

                setAssetTemplateColumnData((prev) => {
                  const newAssets: AssetSelection[] = [];

                  prev.familyAssetIds?.forEach((fid) => {
                    selectedAssets.forEach((aname) => {
                      // only push if that asset belongs to this family
                      const found = assetName.find(
                        (a) => a.value === aname && a.familyAssetId === fid
                      );
                      if (found) {
                        newAssets.push({ familyAssetId: fid, assetName: aname });
                      }
                    });
                  });

                  return {
                    ...prev,
                    assets: newAssets,
                  };
                });
              }}

            />



          </div>

          {/* Attach File */}
          <div className="mb-3">
            <h6>Attach File</h6>
            <Input
              id="fileUpload"
              type="file"
              inputProps={{ multiple: true, accept: "image/*" }}
              onChange={handleFileUpload}
              style={{ fontSize: "14px" }}
            />
          </div>

          {/* File List */}
          {assetTemplateColumnData?.attachments?.length > 0 && (
            <div className="mb-3">
              <Box
                sx={{
                  width: "100%",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  p: 1,
                  backgroundColor: "#f9f9f9",
                }}
              >
                <List sx={{ width: "100%", bgcolor: "background.paper", p: 1 }}>
                  {assetTemplateColumnData.attachments.map((file, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        borderBottom: "1px solid #ddd",
                        pb: 1,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: "break-word", flexGrow: 1 }}
                      >
                        {file.name}
                      </Typography>
                      <IconButton
                        onClick={() => {
                          const fileUrl = `${FILE_DOWNLOAD_PREVIEW + file.attachmentId}`;
                          const link = document.createElement("a");
                          link.href = fileUrl;
                          link.setAttribute("download", "");
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        <DownloadIcon color="primary" />
                      </IconButton>
                      <IconButton onClick={() => removeFile(index)}>
                        <CloseIcon color="error" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "30px",
            }}
          >
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"
              type="button"
            >
              Cancel
            </ReusablePrimeButton>
            {userDetails.role.some(
              (r) =>
                r.roleName === "Admin" ||
                r.roleName === "Service Template Column Write"
            ) && (
                <ReusablePrimeButton
                  className="ms-1"
                  severity="info"
                  onClick={handleSubmit}
                >
                  Submit
                </ReusablePrimeButton>
              )}
          </div>
        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};



const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: "",
    width: "",
    margin: "50px auto",
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "84px",
    backgroundColor: "#f9f9f9",
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
  },
  heading: {
    textAlign: "center",
    color: "#333",
    marginBottom: "20px",
    fontSize: "26px",
    fontWeight: "bold",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  formGroup: {
    marginBottom: "20px",
    width: "100%",
  },
  label: {
    fontSize: "16px",
    fontWeight: "bold",
    display: "block",
    marginBottom: "5px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
  },
};

export default TemplateColumn;


